import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { AssetVizMode } from '@asset-viz/defines'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import { useSurfaceStencil } from '@asset-viz/objects/surfaces/util/useSurfaceStencil'
import {
  overlayMaterialDefines,
  RenderLayerID,
  RenderOrder,
} from '@asset-viz/utils/renderingDefines'
import React from 'react'
import { useAssetVizTheme } from '@asset-viz/providers/theme'
import * as THREE from 'three'
import { GeckoComponentPath, GeckoFeature } from 'types'

import { getRandomColor } from './featureDebugging'

export enum SurfaceMeshSelectionType {
  AssetModelEditorSelectable,
  VizToolTipSelectable,
  NotSelectable,
}
/**
 * Features that cuts mesh within a path on any type of geometry.
 * @param path          Path to create mesh
 * @param featureModel         Feature
 * @param selectionType Optional. Default is NotSelectable
 *                      * AssetModelEditorSelectable - Feature should be selectable
 *                            when Asset Model Editor is open
 *                      * VizToolTipSelectable - Feature expected to be passed into
 *                            VizToolTip
 *                      * NotSelectable - Feature will never be selectable
 * @param colorMesh     * Boolean to color mesh. Defaults to false
 * @param fillColor     * Optional custom color string to use when colorMesh is true
 * @returns
 */
export const SurfaceMeshFeature3D: React.FC<{
  path: GeckoComponentPath
  featureModel: GeckoFeature
  selectionType?: SurfaceMeshSelectionType
  showDebugColors?: boolean
  userDataName?: string
  colorMesh?: boolean
  fillColor?: string
  scale?: THREE.Vector3 | [number, number, number]
}> = ({
  path,
  featureModel,
  selectionType = SurfaceMeshSelectionType.NotSelectable,
  showDebugColors = false,
  userDataName = 'extrudingFeature',
  colorMesh = false,
  fillColor,
  scale = new THREE.Vector3(1, 1, 1),
}) => {
  const { showFeatures, mode, clipConfig, assetOpacity } = useAssetVizInternal()
  const theme = useAssetVizTheme()
  const { componentModel } = useSurfaceProps()

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature: featureModel, componentModel }),
    [featureModel, componentModel, showFeatures]
  )

  const firstMesh = useSurfaceStencil(
    path.points,
    path.pathDirectionX,
    path.pathDirectionY
  )

  const { userData, meshColor } = React.useMemo(() => {
    const userData: Record<string, GeckoFeature | undefined> = {}
    userData[userDataName] =
      selectionType !== SurfaceMeshSelectionType.NotSelectable
        ? featureModel
        : undefined
    return {
      userData,
      meshColor: getRandomColor(),
    }
  }, [featureModel, selectionType, userDataName])

  const featureOpacity = React.useMemo(
    () =>
      typeof assetOpacity === 'number'
        ? assetOpacity
        : assetOpacity(componentModel),
    [assetOpacity, componentModel]
  )

  const renderLayer = React.useMemo(() => {
    if (showThisFeature) {
      if (colorMesh) return RenderLayerID.VisibleCollidable
      if (showDebugColors) return RenderLayerID.VisibleNonCollidable
      else if (
        (mode === AssetVizMode.AllFeaturesSelectable &&
          selectionType ===
            SurfaceMeshSelectionType.AssetModelEditorSelectable) ||
        selectionType === SurfaceMeshSelectionType.VizToolTipSelectable
      )
        return RenderLayerID.InvisibleCollidable
    }

    return RenderLayerID.InvisibleNonCollidable
  }, [showThisFeature, mode, showDebugColors, selectionType, colorMesh])

  return (
    <>
      <mesh
        layers={renderLayer}
        name={`${featureModel.id}_mesh_${featureModel.id}_${0}`}
        geometry={firstMesh}
        renderOrder={RenderOrder.Debug}
        userData={userData}
        scale={scale}>
        <meshBasicMaterial
          color={colorMesh ? fillColor || theme.background2_5 : meshColor}
          side={THREE.DoubleSide}
          clippingPlanes={clipConfig.planes}
          opacity={featureOpacity}
          {...overlayMaterialDefines}
        />
      </mesh>
    </>
  )
}
